import { useT } from '../../i18n/useT'
import type { ThemeChoice } from '../../lib/theme'
import { useTheme } from '../../hooks/useTheme'

/** Sun, moon, monitor — drawn in the chrome's own icon language: 24-unit
 * box, stroked, 1.8 weight, currentColor. */
function Icon({ choice, className }: { choice: ThemeChoice; className?: string }) {
  const common = {
    className,
    fill: 'none' as const,
    viewBox: '0 0 24 24',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    'aria-hidden': true,
  }
  if (choice === 'light') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="4" />
        <path
          strokeLinecap="round"
          d="M12 3v2m0 14v2M3 12h2m14 0h2M5.6 5.6l1.4 1.4m10 10l1.4 1.4m0-12.8l-1.4 1.4m-10 10l-1.4 1.4"
        />
      </svg>
    )
  }
  if (choice === 'dark') {
    return (
      <svg {...common}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M20.5 14.6A8.5 8.5 0 019.4 3.5a8.5 8.5 0 1011.1 11.1z"
        />
      </svg>
    )
  }
  return (
    <svg {...common}>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path strokeLinecap="round" d="M9 20h6m-3-4v4" />
    </svg>
  )
}

const CHOICES: ThemeChoice[] = ['light', 'dark', 'system']

/**
 * The theme, in the footer.
 *
 * It started beside the language globe in the header and moved here, which
 * is where a preference that is set once and then left alone belongs — the
 * header is for what you do on this page. One control rather than a desktop
 * dropdown plus a phone variant: the footer is on every page at every width,
 * the same reason "Cookie settings" lives in it.
 *
 * The words are in the tooltip, not on the segments. An icon alone IS a
 * guess — a moon could be "night mode", "do not disturb" or "sleep", and the
 * third state has no icon that says it out loud at all — so the label the
 * button no longer prints is what `title` and `aria-label` carry: hover says
 * it, a screen reader says it, and the group keeps its own "Theme" caption
 * so the three icons are at least placed. Touch has no hover and gets the
 * caption plus the convention; that is the trade the compact row costs.
 *
 * A radiogroup rather than three buttons: one of the three is always the
 * answer, which is what `aria-checked` means and what `aria-pressed` does
 * not.
 *
 * Sized to the footer, not to the page: a 26 px row of square segments, the
 * density of the link columns beneath it. Larger, it read as the footer's
 * main event, and it sits UNDER the wordmark rather than across from it —
 * the two have nothing to do with each other, and opposite ends of one line
 * reads as a pair.
 */
export function ThemePicker() {
  const t = useT()
  const { choice, setTheme } = useTheme()
  return (
    <div className="flex items-center gap-2.5">
      <span className="text-xs text-text-secondary">{t('ui.theme')}</span>
      <div
        role="radiogroup"
        aria-label={t('ui.theme')}
        className="flex rounded-lg border border-border overflow-hidden"
      >
        {CHOICES.map((c) => (
          <button
            key={c}
            role="radio"
            aria-checked={c === choice}
            aria-label={t(`theme.${c}`)}
            title={t(`theme.${c}`)}
            onClick={() => setTheme(c)}
            className={`flex items-center p-1.5 transition-colors cursor-pointer ${
              c === choice
                ? 'bg-primary text-on-accent'
                : 'text-text-secondary hover:bg-surface-hover hover:text-text'
            }`}
          >
            <Icon choice={c} className="w-3.5 h-3.5" />
          </button>
        ))}
      </div>
    </div>
  )
}
